// import { DATE_OFFSET, VEM_URL } from 'services';

import { TireInspection } from 'pages/new-transaction/tires';
import { Damage, Transaction, TransactionImage, TransactionRow } from 'types';

import { TransactionActivity } from 'types/audit/transaction-activity';
import { joinRsql, LogicalOperator } from 'utils';
import { refreshToken } from './auth';
import { downloadCsv } from './download-csv';
import { fetchPaginated } from './fetch-paginated';
import fetchWithRetry from './fetch-retry';

export const fetchTransactionsList = fetchPaginated<TransactionRow>('transactions');

export const fetchDrafts = fetchPaginated<TransactionRow>('transactions/drafts');

type TransactionSpecification = {
  deleted?: false;
  bookingId?: number | string;
  unitNumber?: number | string;
};

const appendSpecification = (filterString?: string, specification?: TransactionSpecification) =>
  joinRsql(
    LogicalOperator.AND,
    filterString,
    specification?.deleted !== undefined && `deleted==${specification.deleted}`,
    !!specification?.bookingId &&
      `(gateInUnits.booking.id==${specification.bookingId},gateOutUnits.booking.id==${specification.bookingId})`,
    /* redundant primary unit number filters to include deleted transactions */
    !!specification?.unitNumber &&
      `(gateInUnits.unitNumber=="${specification.unitNumber}",\
        gateOutUnits.unitNumber=="${specification.unitNumber}",\
        primaryChassis.unitNumber=="${specification.unitNumber}",\
        primaryContainer.unitNumber=="${specification.unitNumber}",\
        primaryGenset.unitNumber=="${specification.unitNumber}")`
  );

export const fetchTransactionsWithSpecifcation =
  (specification: TransactionSpecification): ReturnType<typeof fetchPaginated<TransactionRow>> =>
  async params => {
    const filterString = appendSpecification(params.filterString, specification);
    return fetchPaginated<TransactionRow>('transactions')({ ...params, filterString });
  };

export const downloadTransactionsCsv = (specification: TransactionSpecification): ReturnType<typeof downloadCsv> => {
  return async (...params) => {
    const filterString = appendSpecification(params[1], specification);
    return downloadCsv(`transactions`)(params[0], filterString, params[2], params[3], params[4]);
  };
};

export const getTransactionsByBookingNumber = async (bookingNumber: string): Promise<TransactionRow[]> =>
  await fetchWithRetry<TransactionRow[]>(`/api/transactions/bookingNumber/${bookingNumber}`, { method: 'GET' }, []);

export const getTransactionById = async (id: number): Promise<Transaction | null> =>
  await fetchWithRetry<Transaction | null>(`/api/transactions/${id}`, { method: 'GET' }, null);

export const postTransaction = async (transaction: Partial<Transaction>): Promise<Transaction | null> => {
  const url = new URL('/api/transactions', window.location.origin);
  const opts: RequestInit = {
    method: 'POST',
    headers: new Headers([['Content-Type', 'application/json']]),
    body: JSON.stringify(transaction)
  };
  return await fetchWithRetry<Transaction | null>(url, opts, null);
};

export const postDraft = async (transaction: Partial<Transaction>): Promise<Transaction | null> => {
  const url = new URL('/api/transactions/drafts', window.location.origin);
  const opts: RequestInit = {
    method: 'POST',
    headers: new Headers([['Content-Type', 'application/json']]),
    body: JSON.stringify(transaction)
  };
  return await fetchWithRetry<Transaction | null>(url, opts, null);
};

export const completeDraft = async ({ id, ...transaction }: Partial<Transaction>): Promise<Transaction | null> => {
  const url = new URL(`/api/transactions/drafts/${id}`, window.location.origin);
  const opts: RequestInit = {
    method: 'PUT',
    headers: new Headers([['Content-Type', 'application/json']]),
    body: JSON.stringify(transaction)
  };
  return await fetchWithRetry<Transaction | null>(url, opts, null);
};

export const getDamage = async (id: number): Promise<Damage[]> =>
  await fetchWithRetry<Damage[]>(`/api/transactions/${id}/damage`, { method: 'GET' }, []);

export const postDamages = async (transactionId: number, damage: Damage) =>
  await fetchWithRetry<any>(
    `/api/transactions/${transactionId}/damages`,
    {
      method: 'POST',
      headers: new Headers([['Content-Type', 'application/json']]),
      body: JSON.stringify(damage)
    },
    []
  );

export const postTires = async (transactionId: number, tire: TireInspection) =>
  await fetchWithRetry<any>(
    `/api/transactions/${transactionId}/tires`,
    {
      method: 'POST',
      headers: new Headers([['Content-Type', 'application/json']]),
      body: JSON.stringify(tire)
    },
    []
  );

/* TODO: double check expected type. maybe TireInspections (many TireInspection) */
export const getTireInspections = async (id: number): Promise<TireInspection[]> =>
  await fetchWithRetry<TireInspection[]>(`/api/transactions/${id}/tires`, { method: 'GET' }, []);

/* TODO: double check expected type */
export const getImages = async (id: number): Promise<TransactionImage> =>
  await fetchWithRetry<any>(`/api/transactions/${id}/images`, { method: 'GET' }, {});

export const postImages = async (
  transactionId: number,
  image: Required<Pick<TransactionImage, 'imageBase64' | 'index'>>
) =>
  await fetchWithRetry<any>(
    `/api/transactions/${transactionId}/images`,
    {
      method: 'POST',
      headers: new Headers([['Content-Type', 'application/json']]),
      body: JSON.stringify(image)
    },
    []
  );

export const getImage = async (imageKey: string) =>
  await fetchWithRetry<string | null>(`/api/images/${imageKey}`, { method: 'GET' }, null);

export const updateTransaction = async (transaction: Transaction): Promise<Transaction | null> => {
  const init: RequestInit = {
    method: 'PUT',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(transaction)
  };
  return await fetchWithRetry(`/api/transactions/${transaction.id}`, init, null);
};

export const deleteTransaction = async (id: number): Promise<void> =>
  await fetchWithRetry<void>(`/api/transactions/${id}`, { method: 'DELETE' }, (() => {})());

export const downloadInterchangePdf = async (transactionId: number) => {
  const url = new URL(`/api/transactions/${transactionId}/interchange`, window.location.origin);
  let token = localStorage.getItem('token');
  const headers = new Headers([
    ['Accept', 'application/pdf'],
    ['Authorization', `Bearer ${token}`]
  ]);

  try {
    let response = await fetch(url, { method: 'GET', headers });
    if (response.status === 401 || response.status === 403) {
      token = await refreshToken();
      if (!token) throw new Error('Unauthenticated');
      headers.set('Authorization', `Bearer ${token}`);
      response = await fetch(url, { method: 'GET', headers });
    }

    if (response.ok) {
      let blob = await response.blob();
      const headers = response.headers;

      const filename = headers
        .get('Content-Disposition')
        ?.split(';')
        .find(h => h.includes('filename'))
        ?.split('=')
        .at(1)
        ?.replace(/"/g, '');

      const fileUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename ?? 'interchange.pdf';
      link.click();
      link.remove();
    }
  } catch (error) {
    console.error(error);
  }
};

export const viewInterchangePdf = async (transactionId: number) => {
  const url = new URL(`/api/transactions/${transactionId}/interchange`, window.location.origin);
  let token = localStorage.getItem('token');
  const headers = new Headers([['Accept', 'application/pdf']]);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  try {
    let response = await fetch(url, { method: 'GET', headers });
    if ((response.status === 401 || response.status === 403) && token) {
      token = await refreshToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
        response = await fetch(url, { method: 'GET', headers });
      }
    }

    if (response.ok) {
      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      window.open(fileUrl, '_blank');
    }
  } catch (error) {
    console.error(error);
  }
};

export const sendInterchangeEmail = async (transactionId: number) => {
  await fetchWithRetry<any>(`/api/transactions/${transactionId}/email`, { method: 'POST' }, {});
};

export const resendEdiForTransaction = async (transactionId: number, recipientId: number) => {
  await fetchWithRetry<void>(
    `/api/transactions/${transactionId}/edi-recipients/${recipientId}`,
    { method: 'POST' },
    undefined
  );
};

export const getTransactionActivity = async (id: number): Promise<TransactionActivity[]> => {
  const url = new URL(`/api/transactions/${id}/activity`, window.location.origin);
  return await fetchWithRetry<TransactionActivity[]>(url, { method: 'GET' }, []);
};
